/**
 * Meta keywords per sida.
 * Notera: Google använder inte meta keywords. Listan finns för att uppfylla
 * on-page-checklistan och hålls kort (5–8 termer) och hämtad ur sidans eget innehåll.
 */
export const PAGE_KEYWORDS: Record<string, string[]> = {
  "/": [
    "filmproduktion",
    "film på abonnemang",
    "reklamfilm",
    "företagsfilm",
    "videoproduktion",
    "filmbyrå Sverige",
  ],
  "/for-vem/some-team": [
    "film för sociala medier",
    "content till social media-team",
    "reels och shorts",
    "paid social-film",
    "videoproduktion sociala medier",
    "kortformat film",
  ],
  "/employer-branding-film": [
    "employer branding film",
    "rekryteringsfilm",
    "arbetsgivarvarumärke video",
    "medarbetarfilm",
    "kulturfilm företag",
    "employer branding video pris",
  ],
  "/branscher/saas": [
    "SaaS-video",
    "produktdemo video",
    "explainer film SaaS",
    "B2B-film",
    "onboardingfilm SaaS",
    "techfilm",
  ],
  "/branscher/e-handel": [
    "e-handelsfilm",
    "produktfilm e-handel",
    "reklamfilm för webbshop",
    "paid social e-handel",
    "produktvideo",
    "konverterande videoannonser",
  ],
  "/kontakt": [
    "kontakta Fenny",
    "boka filmproduktion",
    "offert film",
    "filmbyrå kontakt",
    "produktionsbolag Stockholm",
  ],
  "/fragor/vad-ar-film-pa-abonnemang": [
    "film på abonnemang",
    "filmabonnemang",
    "video subscription",
    "löpande filmproduktion",
    "månadspris film",
    "Pulse-abonnemang",
  ],
  "/faq": [
    "vanliga frågor film",
    "filmproduktion FAQ",
    "vad kostar film",
    "leveranstid film",
    "filmabonnemang frågor",
  ],
};

/** Returnerar en meta-post för keywords, eller tom lista om sidan saknar nyckelord. */
export function keywordsMeta(path: string): Array<{ name: string; content: string }> {
  const list = PAGE_KEYWORDS[path];
  return list ? [{ name: "keywords", content: list.join(", ") }] : [];
}

/* ------------------------------------------------------------------ *
 * Automatisk generering
 *
 * 595 sidor saknade keywords i byråns rapport. Listorna skrivs inte för
 * hand — de härleds ur sidans egen sökväg (affärsområde + ämne + ort) och
 * titeln. PAGE_KEYWORDS ovan är fortfarande manuell överstyrning.
 * ------------------------------------------------------------------ */

/** Första segmentet → basnyckelord för affärsområdet. */
const SECTION_TERMS: Record<string, string[]> = {
  film: ["filmproduktion", "företagsfilm"],
  filmproduktion: ["filmproduktion", "produktionsbolag"],
  filmbyra: ["filmbyrå", "filmproduktion"],
  videobyra: ["videobyrå", "videoproduktion"],
  produktionsbolag: ["produktionsbolag", "filmproduktion"],
  foretagsfilm: ["företagsfilm", "filmproduktion företag"],
  reklamfilm: ["reklamfilm", "reklamfilmsproduktion"],
  heritage: ["livsberättelse på film", "minnesfilm", "kunskapsarv"],
  prime: ["filmproduktion premium", "reklamfilmsproduktion"],
  pulse: ["film på abonnemang", "filmabonnemang"],
  kunskap: ["filmproduktion", "guide film"],
  guider: ["guide filmproduktion", "checklista film"],
  fragor: ["filmproduktion", "vanliga frågor film"],
  pris: ["pris filmproduktion", "vad kostar film"],
  priser: ["pris filmproduktion", "filmpriser"],
  jamfor: ["jämförelse filmproduktion", "välja filmbyrå"],
  case: ["case filmproduktion", "exempel företagsfilm"],
  branscher: ["branschfilm", "filmproduktion för företag"],
  branschstudier: ["branschstudie film", "statistik filmproduktion"],
  en: ["video production sweden", "swedish video agency"],
};

const STOPWORDS = new Set([
  "och",
  "att",
  "som",
  "vad",
  "hur",
  "en",
  "ett",
  "the",
  "for",
  "med",
  "pa",
  "i",
  "av",
  "om",
  "ar",
  "vs",
  "till",
]);

function humanize(slug: string): string {
  return decodeURIComponent(slug)
    .split("-")
    .filter((w) => w && !STOPWORDS.has(w.toLowerCase()))
    .join(" ")
    .trim();
}

/**
 * Härleder 4–7 nyckelord för en sökväg. Returnerar tom lista för
 * systemvägar och filer.
 */
export function generateKeywords(path: string, title?: string): string[] {
  const clean = path.split("?")[0]!.split("#")[0]!.replace(/\/$/, "") || "/";
  if (clean.includes(".") || clean.startsWith("/api") || clean.startsWith("/admin")) return [];

  const manual = PAGE_KEYWORDS[clean];
  if (manual) return manual;

  const segments = clean.split("/").filter(Boolean);
  if (segments.length === 0) return PAGE_KEYWORDS["/"] ?? [];

  const out: string[] = [];
  const push = (value: string) => {
    const v = value.trim();
    if (!v || v.length < 3) return;
    if (out.some((existing) => existing.toLowerCase() === v.toLowerCase())) return;
    out.push(v);
  };

  const english = segments[0] === "en";
  const sectionKey = english ? (segments[1] ?? "en") : segments[0]!;
  const topicSlug = segments[segments.length - 1]!;
  const topic = humanize(topicSlug);

  if (topic) push(topic);
  if (segments.length > 1) {
    const parent = humanize(segments[segments.length - 2]!);
    if (parent && parent !== topic) push(`${topic} ${parent}`.trim());
  }

  for (const term of SECTION_TERMS[sectionKey] ?? SECTION_TERMS[segments[0]!] ?? []) push(term);

  // Ord ur titeln fångar ort- och formatbegrepp som inte syns i sökvägen.
  if (title) {
    const head = title.split("|")[0]!.replace(/[—–-]/g, " ");
    for (const phrase of head.split(/[,.]/)) {
      const words = phrase.trim().split(/\s+/).filter(Boolean);
      if (words.length >= 2 && words.length <= 4) push(words.join(" ").toLowerCase());
    }
  }

  if (out.length < 3) push(english ? "video production" : "filmproduktion");

  return out.slice(0, 7);
}

/** Färdig `content`-sträng för <meta name="keywords">. */
export function keywordsContent(path: string, title?: string): string {
  return generateKeywords(path, title).join(", ");
}
